import * as React from 'react';
import PropTypes from 'prop-types';
import invariant from 'invariant';
import { SortableContext } from '../SortableContainer';

import { provideDisplayName, omit } from '../utils';

const propTypes = {
  index: PropTypes.number.isRequired,
  collection: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  disabled: PropTypes.bool,
};

const omittedProps = Object.keys(propTypes);

export default function sortableElement(
  WrappedComponent,
  config = { withRef: false },
) {
  function WithSortableElement(props) {
    const context = React.useContext(SortableContext);
    const domRef = React.useRef(null);
    const wrappedInstance = React.useRef(null);
    const { collection = 0, disabled, index } = props;

    React.useEffect(() => {
      if (!domRef.current) return;
      const node = domRef.current;
      
      node.sortableInfo = {
        collection,
        disabled,
        index,
        manager: context.manager,
      };

      const ref = { node };
      context.manager.add(collection, ref);

      return () => {
        context.manager.remove(collection, ref);
      };
    }, [collection, disabled, index, context.manager]);

    React.useImperativeHandle(props.forwardedRef, () => ({
      getWrappedInstance() {
        invariant(
          config.withRef,
          'To access the wrapped instance, you need to pass in {withRef: true} as the second argument of the SortableElement() call',
        );
        return wrappedInstance.current;
      }
    }));

    const ref = config.withRef ? wrappedInstance : null;

    return <WrappedComponent ref={ref} domRef={domRef} {...omit(props, omittedProps)} />;
  }

  WithSortableElement.displayName = provideDisplayName(
    'sortableElement',
    WrappedComponent,
  );
  WithSortableElement.propTypes = propTypes;
  WithSortableElement.defaultProps = {
    collection: 0,
  };

  return React.forwardRef((props, ref) => {
    return <WithSortableElement {...props} forwardedRef={ref} />;
  });
}
